import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";
import { canFinanceWrite } from "helpers/financePermissions";
import { useAccess } from "hooks/useAccess";
import {
  cancelarTituloPagar,
  cancelarTituloReceber,
} from "../services/cancelamento-titulo.service";
import type {
  CancelamentoTituloDialogState,
  CancelamentoTituloForm,
  TipoCancelamentoTitulo,
} from "../types/cancelamento-titulo.types";
import type { ContaReceberView } from "../types/contas-receber.types";
import type { ContaPagarView } from "../types/contas-pagar.types";

function formPadrao(): CancelamentoTituloForm {
  return {
    motivo: "",
    observacao: "",
  };
}

function dialogFechado(): CancelamentoTituloDialogState {
  return {
    open: false,
    tipo: "RECEBER",
    tituloId: "",
  };
}

export type CancelamentoTituloFeedback =
  | { severity: "success"; message: string }
  | null;

export type UseCancelamentoTituloOptions = {
  onSuccess?: () => void;
};

export type UseCancelamentoTituloResult = {
  podeCancelarTitulo: boolean;
  dialog: CancelamentoTituloDialogState;
  form: CancelamentoTituloForm;
  setForm: Dispatch<SetStateAction<CancelamentoTituloForm>>;
  loading: boolean;
  error: string | null;
  feedback: CancelamentoTituloFeedback;
  success: boolean;
  clearFeedback: () => void;
  abrirCancelamentoReceber: (titulo: ContaReceberView) => void;
  abrirCancelamentoPagar: (titulo: ContaPagarView) => void;
  fecharDialog: () => void;
  confirmarCancelamento: () => Promise<void>;
};

export function useCancelamentoTitulo(
  options: UseCancelamentoTituloOptions = {},
): UseCancelamentoTituloResult {
  const { onSuccess } = options;
  const access = useAccess();

  const podeCancelarTitulo = useMemo(
    () => canFinanceWrite(access),
    [access]
  );

  const [dialog, setDialog] = useState<CancelamentoTituloDialogState>(() =>
    dialogFechado(),
  );
  const [form, setForm] = useState<CancelamentoTituloForm>(() => formPadrao());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<CancelamentoTituloFeedback>(null);

  const success = feedback?.severity === "success";

  const clearFeedback = useCallback(() => {
    setFeedback(null);
  }, []);

  const fecharDialog = useCallback(() => {
    setDialog(dialogFechado());
    setError(null);
    setLoading(false);
  }, []);

  const abrirCancelamentoReceber = useCallback((titulo: ContaReceberView) => {
    setFeedback(null);
    setError(null);
    setDialog({
      open: true,
      tipo: "RECEBER",
      tituloId: titulo.id,
      tituloDescricao: titulo.descricao,
      tituloDetalhe: titulo.clienteNome,
      valorAberto: titulo.valorAberto,
      dataVencimento: titulo.dataVencimento,
      status: titulo.status,
    });
    setForm(formPadrao());
  }, []);

  const abrirCancelamentoPagar = useCallback((titulo: ContaPagarView) => {
    setFeedback(null);
    setError(null);
    setDialog({
      open: true,
      tipo: "PAGAR",
      tituloId: titulo.id,
      tituloDescricao: titulo.descricao,
      tituloDetalhe: titulo.fornecedorNome,
      valorAberto: titulo.valorAberto,
      dataVencimento: titulo.dataVencimento,
      status: titulo.status,
    });
    setForm(formPadrao());
  }, []);

  const confirmarCancelamento = useCallback(async () => {
    if (!dialog.open || !dialog.tituloId) return;

    setError(null);
    const motivo = form.motivo.trim();
    if (motivo.length < 5) {
      setError("Motivo deve ter pelo menos 5 caracteres.");
      return;
    }

    const observacaoExtra =
      form.observacao?.trim() !== "" ? form.observacao.trim() : undefined;

    setLoading(true);

    try {
      const tipo: TipoCancelamentoTitulo = dialog.tipo;
      if (tipo === "RECEBER") {
        const { data, status, error: err } = await cancelarTituloReceber(
          dialog.tituloId,
          { motivo, observacao: observacaoExtra },
        );
        if (status === 200 && data?.ok) {
          fecharDialog();
          setFeedback({
            severity: "success",
            message: data.mensagem ?? "Cancelamento registrado com sucesso.",
          });
          onSuccess?.();
          return;
        }
        setError(err ?? data?.mensagem ?? "Não foi possível cancelar o título.");
        return;
      }

      const { data, status, error: err } = await cancelarTituloPagar(
        dialog.tituloId,
        { motivo, observacao: observacaoExtra },
      );
      if (status === 200 && data?.ok) {
        fecharDialog();
        setFeedback({
          severity: "success",
          message: data.mensagem ?? "Cancelamento registrado com sucesso.",
        });
        onSuccess?.();
        return;
      }
      setError(err ?? data?.mensagem ?? "Não foi possível cancelar o título.");
    } finally {
      setLoading(false);
    }
  }, [dialog, form, fecharDialog, onSuccess]);

  return {
    podeCancelarTitulo,
    dialog,
    form,
    setForm,
    loading,
    error,
    feedback,
    success,
    clearFeedback,
    abrirCancelamentoReceber,
    abrirCancelamentoPagar,
    fecharDialog,
    confirmarCancelamento,
  };
}
